// src/pages/Services/ServicosPage.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Assignment as AssignmentIcon,
  Analytics as AnalyticsIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  DeviceHub as DeviceHubIcon,
  Psychology as PsychologyIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';

const ServiceCard = ({ icon: Icon, title, description, features, buttonText, buttonLink }) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: 8,
      },
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Icon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
        <Typography variant="h5" component="h3">
          {title}
        </Typography>
      </Box>
      <Typography variant="body1" paragraph color="text.secondary">
        {description}
      </Typography>
      <List>
        {features.map((feature, index) => (
          <ListItem key={index} sx={{ px: 0 }}>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={feature} />
          </ListItem>
        ))}
      </List>
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Button variant="contained" fullWidth component={Link} to={buttonLink} size="large">
          {buttonText}
        </Button>
      </Box>
    </CardContent>
  </Card>
);

ServiceCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonLink: PropTypes.string.isRequired,
};

const ServicosPage = () => {
  // ——— HERO FEATURES (inclui TCC e IA/Anti-Plágio) ———
  const features = [
    {
      icon: SpeedIcon,
      title: 'Rápido e Eficiente',
      description: 'Processo optimizado do início ao fim',
    },
    {
      icon: SecurityIcon,
      title: '100% Seguro',
      description: 'Dados protegidos e criptografados',
    },
    {
      icon: PsychologyIcon,
      title: 'IA & Anti-plágio',
      description: 'Detecção de IA e verificação de similaridade',
    },
    {
      icon: AssignmentIcon,
      title: 'Gestão de TCCs',
      description: 'Fluxo completo e KPIs em tempo real',
    },
    {
      icon: SupportIcon,
      title: 'Suporte Dedicado',
      description: 'Atendimento personalizado',
    },
    {
      icon: DeviceHubIcon,
      title: 'Integrado',
      description: 'Sistema totalmente conectado',
    },
  ];

  // ——— CARDS PRINCIPAIS (novos: TCC & IA/Anti-Plágio) ———
  const services = [
    {
      icon: PersonIcon,
      title: 'Para Estudantes',
      description: 'Encontre e candidate-se às melhores oportunidades educacionais.',
      features: [
        'Acesso a todas as campanhas disponíveis',
        'Processo de pré-inscrição simplificado',
        'Acompanhamento em tempo real',
        'Notificações personalizadas',
      ],
      buttonText: 'Começar Agora',
      buttonLink: '/registro/estudante',
    },
    {
      icon: SchoolIcon,
      title: 'Para Instituições',
      description: 'Gerencie campanhas, pré-inscrições e processos acadêmicos de ponta a ponta.',
      features: [
        'Gestão completa de campanhas',
        'Dashboard administrativo',
        'Relatórios detalhados',
        'Suporte dedicado',
      ],
      buttonText: 'Tornar-se Parceiro',
      buttonLink: '/registro/instituicao',
    },
    {
      icon: AssignmentIcon,
      title: 'TCC & Monografias',
      description:
        'Painel completo para acompanhar propostas, projetos, monografias e defesas.',
      features: [
        'KPIs por etapa (proposta → projecto → monografia → defesa)',
        'Agenda de defesas e gestão de júris',
        'Produtividade por supervisor e por departamento',
        'Monitor de atrasos e SLA com alertas',
        'Exportação (PDF, Excel, Word, JSON)',
      ],
      buttonText: 'Abrir Dashboard TCC',
      buttonLink: '/dashboard-tcc',
    },
    {
      icon: PsychologyIcon,
      title: 'IA & Anti-Plágio',
      description:
        'Detecte escrita gerada por IA e similaridade textual com central de risco.',
      features: [
        'IA score por documento e classificação de risco',
        'Alertas e histórico de verificações',
        'Painel unificado (plágio, IA e notas)',
        'Relatórios e exportação para auditoria',
      ],
      buttonText: 'Verificar Documentos',
      buttonLink: '/dashboard-tcc?tab=alertas',
    },
    {
      icon: PaymentIcon,
      title: 'Gestão de Pagamentos',
      description: 'Sistema integrado de pagamentos e controlo financeiro.',
      features: [
        'Múltiplos métodos de pagamento',
        'Geração automática de recibos',
        'Relatórios financeiros',
        'Histórico completo',
      ],
      buttonText: 'Saber Mais',
      buttonLink: '/servicos/pagamentos',
    },
    {
      icon: AnalyticsIcon,
      title: 'Análises Avançadas',
      description: 'Obtenha insights com dashboards e relatórios personalizáveis.',
      features: [
        'Dashboards dinâmicos',
        'Comparativos por curso/departamento',
        'Visualizações interativas',
        'Exportação de dados facilitada',
      ],
      buttonText: 'Ver Análises',
      buttonLink: '/analises',
    },
  ];

  const statistics = [
    { value: '5+', label: 'Instituições Parceiras' },
    { value: '1k+', label: 'Estudantes Ativos' },
    { value: '95%', label: 'Taxa de Satisfação' },
    { value: '24/7', label: 'Suporte Disponível' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" gutterBottom>
                Nossos Serviços
              </Typography>
              <Typography variant="h5" paragraph>
                Soluções completas para instituições e estudantes
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                sx={{ mt: 2 }}
                component={Link}
                to="/registro"
              >
                Começar Agora
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                {features.map((feature) => (
                  <Paper
                    key={feature.title}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      width: '45%',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <feature.icon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2">{feature.description}</Typography>
                    <Chip label="Novo" color="secondary" size="small" sx={{ mt: 1 }} />
                  </Paper>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider sx={{ my: 8 }} />

      {/* Estatísticas */}
      <Container maxWidth="lg" sx={{ mt: -5 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Grid container spacing={3} justifyContent="center">
            {statistics.map((stat) => (
              <Grid item xs={6} sm={3} key={stat.label}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" gutterBottom>
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      <Divider sx={{ my: 8 }} />

      {/* Principais Serviços */}
      <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
        <Typography variant="h3" align="center" gutterBottom>
          Nossos Serviços
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          Soluções completas para todos os envolvidos no processo educacional
        </Typography>
        <Grid container spacing={4} sx={{ mt: 4 }}>
          {services.map((service) => (
            <Grid item xs={12} md={4} key={service.title}>
              <ServiceCard {...service} />
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider sx={{ my: 8 }} />

      {/* Recursos Adicionais */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom>
            Recursos Avançados
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom>
                  Para Estudantes
                </Typography>
                <List>
                  {[
                    'Busca avançada de cursos e instituições',
                    'Notificações personalizadas sobre prazos',
                    'Sistema de recomendações inteligente',
                    'Acompanhamento do status em tempo real',
                    'Armazenamento seguro de documentos',
                  ].map((feature, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <StarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
                <Chip label="Estudante" color="primary" />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom>
                  Para Instituições
                </Typography>
                <List>
                  {[
                    'Dashboard administrativo completo',
                    'Sistemas de pagamentos',
                    'Ferramentas de análise e relatórios',
                    'Gestão de campanhas e vagas',
                    'Sistema de comunicação integrado',
                    'TCC & Monografias: dashboard com KPIs e status por etapa',
                    'Fluxo completo de TCC (proposta → projecto → monografia → defesa)',
                    'Agenda de defesas e gestão de júris',
                    'Produtividade por supervisor e por departamento',
                    'Monitor de atrasos e SLA com alertas',
                    'Exportação de relatórios (PDF, Excel, Word e JSON)',
                    'Detecção de escrita por IA (IA score) e classificação de risco',
                    'Anti-plágio: verificação de similaridade textual',
                    'Painel unificado de risco (plágio, IA e notas)',
                    'Automação de processos',
                  ].map((feature, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <StarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
                <Chip label="Instituição" color="primary" />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider sx={{ my: 8 }} />

      {/* Call to Action */}
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white',
          }}
        >
          <Typography variant="h4" gutterBottom>
            Comece Agora Mesmo
          </Typography>
          <Typography variant="h6" paragraph>
            Junte-se a milhares de usuários que já confiam no ZenKroll
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button variant="contained" color="secondary" size="large" component={Link} to="/registro/estudante">
              Sou Estudante
            </Button>
            <Button variant="outlined" color="inherit" size="large" component={Link} to="/registro/instituicao">
              Sou Instituição
            </Button>
          </Box>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default ServicosPage;
